/**
 * Per-subcategory unread counts in a single request.
 *
 * Both the tray (`NotificationTray`) and the admin list (`InboxTabs`) call
 * this hook; TanStack Query dedupes the request so only one HTTP round-
 * trip lands per `is_unread` slice. Stale time 30s — fresh enough to feel
 * live, cheap enough that nav around the admin doesn't spam the endpoint.
 *
 * Lookup helper smooths the NULL→general roll-up so callers don't have
 * to remember the convention.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchInboxCountsByCategory, type InboxCountsByCategory } from './api';

export const inboxCountsQueryKey = (is_unread: boolean) =>
  ['notifications', 'inbox', 'counts', is_unread ? 'unread' : 'all'] as const;

export const useInboxCountsByCategory = (opts: { is_unread?: boolean; enabled?: boolean } = {}) => {
  const is_unread = opts.is_unread ?? true;
  return useQuery({
    queryKey: inboxCountsQueryKey(is_unread),
    queryFn: () => fetchInboxCountsByCategory({ is_unread }),
    // `enabled` exists for the stream provider, which sits above the router and so is mounted
    // on the auth pages too, where there is no session to count anything for.
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    // No window-focus refetch. It was the recovery path for a silently dead
    // stream, but it fired on every alt-tab after 30s — ops keep many sale
    // tabs open and switch constantly, so one idle tab produced a count call
    // per minute (measured: 8 in 8 minutes with the stream healthy the whole
    // time). Recovery now happens where the gap can actually exist: the
    // stream hook invalidates this query on every RE-connect, and live frames
    // / mark-read invalidate it as before.
    refetchOnWindowFocus: false,
  });
};

export const countFor = (data: InboxCountsByCategory | undefined, bucket: string): number =>
  data?.counts?.[bucket] ?? 0;
